import React from 'react';
import type { Instance } from '@v2/types';
import { InstancesTable, InstanceDetails, InstanceFilters, AddFilterModal } from './components';
import { useInstances, useInstanceManifest, usePims, useTableFilters } from '@v2/hooks';
import { SettingsPageLayout, DetailPanel, ListDetailView } from '@v2/layouts';
import { usePermissionsContext, Permissions } from '@v2/contexts';
import { useInstanceNavigation, useInstanceSelection, useAdvancedFilters } from './hooks';
import { styles } from './styles';

const searchInstances = (item: Instance, term: string): boolean => {
  if (item.id?.toLowerCase().includes(term)) return true;
  if (item.name?.toLowerCase().includes(term)) return true;
  if (item.practice?.name?.toLowerCase().includes(term)) return true;
  return false;
};

const BulkManifestUpdatePage: React.FC = () => {
  const { hasPermission } = usePermissionsContext();
  const canAccess = hasPermission([Permissions.BulkManifestUpdate]);

  const { selectedInstanceId, handleSelectInstance, handleClosePanel } = useInstanceNavigation();

  const {
    instances,
    loading: instancesLoading,
    error: instancesError,
    refetch: refetchInstances,
  } = useInstances();

  const { pims } = usePims();

  const {
    filters,
    pimsFilter,
    setPimsFilter,
    pimsOptions,
    isAddFilterModalOpen,
    filterToEdit,
    filteredInstances,
    openAddFilterModal,
    openEditFilterModal,
    closeFilterModal,
    handleSubmitFilter,
    handleRemoveFilter,
  } = useAdvancedFilters(instances, pims);

  const { filteredData, search, setSearch, sortConfig, handleSort } = useTableFilters({
    data: filteredInstances,
    searchFn: searchInstances,
    initialSort: { key: 'name', direction: 'asc' },
  });

  const { selectedInstanceIds, handleSelectionChange } = useInstanceSelection();

  const selectedInstance = selectedInstanceId
    ? filteredData.find(i => i.id === selectedInstanceId)
    : null;

  const {
    manifest,
    loading: manifestLoading,
    error: manifestError,
    refetch: refetchManifest,
  } = useInstanceManifest(selectedInstanceId);

  if (!canAccess) {
    return (
      <SettingsPageLayout title="Bulk Manifest Update">
        <div className={styles.noPermission}>You do not have permission to access this page.</div>
      </SettingsPageLayout>
    );
  }

  return (
    <SettingsPageLayout title="Bulk Manifest Update">
      <ListDetailView
        selectedId={selectedInstanceId}
        list={
          <InstancesTable
            instances={filteredData}
            loading={instancesLoading}
            error={instancesError}
            onSelectInstance={handleSelectInstance}
            selectedInstanceId={selectedInstanceId || undefined}
            selectedInstanceIds={selectedInstanceIds}
            onSelectionChange={handleSelectionChange}
            sortConfig={sortConfig}
            onSortChange={handleSort}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by ID, instance name, or practice name..."
            filterSlot={
              <InstanceFilters
                pimsOptions={pimsOptions}
                pimsFilter={pimsFilter}
                onPimsFilterChange={setPimsFilter}
                activeFilters={filters}
                onAddFilter={openAddFilterModal}
                onEditFilter={openEditFilterModal}
                onRemoveFilter={handleRemoveFilter}
              />
            }
          />
        }
        detail={
          <DetailPanel
            title={selectedInstance?.practice?.name ?? 'Instance Details'}
            onClose={handleClosePanel}
            loading={manifestLoading}
            loadingMessage="Loading instance manifest..."
            error={manifestError}
            errorMessage="There was an error fetching the instance manifest."
            onRetry={() => {
              refetchInstances();
              refetchManifest();
            }}>
            {selectedInstance && (
              <InstanceDetails
                instance={selectedInstance}
                manifest={manifest}
                manifestLoading={manifestLoading}
              />
            )}
          </DetailPanel>
        }
      />
      <AddFilterModal
        isOpen={isAddFilterModalOpen}
        onClose={closeFilterModal}
        onSubmit={handleSubmitFilter}
        pimsList={pims}
        editFilter={filterToEdit}
      />
    </SettingsPageLayout>
  );
};

export default BulkManifestUpdatePage;
